<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useChat } from '@/store/chat'
import RichMessage from '@/components/RichMessage.vue'
import { GestoDeCita } from '@/deslizarParaCitar'
import type { UiMessage } from '@/types'
import type { CitaPayload } from '@shared/types'

/**
 * `modo` decide si el chat es una burbuja o el producto.
 *
 *   <tc-chat>                → burbuja flotante. Lo que se embebe en los sitios
 *                              de UVAC: vive en una esquina y no estorba.
 *   <tc-chat modo="panel">   → empotrado. Llena el contenedor que lo hospeda y
 *                              nace abierto. Es lo que usa bot.uvaconline.com,
 *                              donde el chat NO es un accesorio de la página:
 *                              es a lo que la gente viene.
 *
 * En móvil el modo panel se convierte en pantalla completa —como escribirle a
 * un asesor por WhatsApp— porque una tercera parte de una pantalla de teléfono
 * no da para conversar.
 */
const props = defineProps<{ modo?: string }>()
const empotrado = computed(() => props.modo === 'panel')

const {
  messages, isOpen, sending, visitorName, datosViaje, carrito, contacto, trabajando,
  init, sendMessage, sendAction, cleanup,
} = useChat()

/** Quién escribe: identifica el mensaje de un vistazo. */
function quien(m: { senderType: string; senderName?: string }): string {
  if (m.senderType === 'visitor') return visitorName.value
  if (m.senderType === 'agent') return m.senderName ?? 'Asesor UVAC'
  return m.senderName ?? 'UVAC'
}
const draft = ref('')
const listEl = ref<HTMLElement | null>(null)
const cajaEl = ref<HTMLTextAreaElement | null>(null)

/**
 * Logo de UVAC en la cabecera.
 *
 * La ruta se resuelve contra el propio `widget.js`, así que funciona igual
 * embebido en uvaconline.com que en cualquier otro sitio: el archivo vive junto
 * al script que lo carga.
 *
 * Si el archivo todavía no está, `logoRoto` lo sustituye por la marca de texto
 * en vez de dejar el ícono de imagen rota — el widget nunca debe verse a medio
 * armar en el sitio de un cliente.
 */
const logoUrl = new URL('logo.png', import.meta.url).href
const logoRoto = ref(false)

onMounted(() => {
  void init()
  // Empotrado en escritorio nace abierto: la columna existe para eso. En móvil
  // no, porque abriría a pantalla completa encima de la portada antes de que el
  // visitante haya visto nada.
  if (empotrado.value && window.matchMedia('(min-width: 1024px)').matches) isOpen.value = true
})
onBeforeUnmount(() => cleanup())

async function scrollToBottom(): Promise<void> {
  await nextTick()
  if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight
}

/**
 * Los mensajes se van revelando; no caen los tres de golpe.
 *
 * Cuando el asesor cotiza, el motor escribe las tres tarjetas y el texto casi
 * en el mismo instante. Aparecían juntos, como un volante metido por debajo de
 * la puerta: nadie lee tres bloques que llegaron todos al mismo tiempo. Un
 * asesor de verdad manda uno, te deja verlo, y sigue con el siguiente.
 *
 * El retraso es solo de pantalla — el dato ya está en Firestore desde el primer
 * momento, así que no se pierde nada si alguien recarga a media revelación.
 */
const revelados = ref(0)
const visibles = computed(() => messages.value.slice(0, revelados.value))
/** Los puntos suspensivos: mientras el motor piensa, y entre ficha y ficha. */
const escribiendo = computed(() => sending.value || revelados.value < messages.value.length)

let pendiente: ReturnType<typeof setTimeout> | null = null

/** Una ficha con fotos pide más tiempo que una línea de texto; lo propio, ninguno. */
function retrasoDe(m: { senderType: string; kind: string }): number {
  if (m.senderType === 'visitor') return 0
  return m.kind === 'text' ? 600 : 950
}

function revelar(): void {
  if (pendiente) return
  const total = messages.value.length
  if (revelados.value > total) revelados.value = total
  if (revelados.value >= total) return

  // Historial: abrir una conversación que ya existía muestra todo de una vez.
  // Revelar de a uno un chat de la semana pasada sería absurdo.
  if (revelados.value === 0 && total > 2) {
    revelados.value = total
    return
  }

  const espera = revelados.value === 0 ? 0 : retrasoDe(messages.value[revelados.value]!)
  pendiente = setTimeout(() => {
    pendiente = null
    revelados.value++
    revelar()
  }, espera)
}

watch(() => messages.value.length, revelar)
watch(revelados, () => { void scrollToBottom() })
watch(escribiendo, () => { void scrollToBottom() })
watch(isOpen, (v) => { if (v) void scrollToBottom() })
onBeforeUnmount(() => { if (pendiente) clearTimeout(pendiente) })

/** Los momentos que rompen el hilo: dejan de ser mensajes y pasan a ser pasos. */
function esHito(m: { kind: string }): boolean {
  return m.kind === 'payment' || m.kind === 'booking_summary'
}

/**
 * El chat narra lo que va pasando; quien lo hospeda decide si le sirve.
 *
 * En bot.uvaconline.com la portada escucha esto y se vuelve una pizarra: pone
 * la foto del paquete que el asesor acaba de proponer, escribe el destino, marca
 * cuando se llegó al pago. Sin este canal la portada solo podía reaccionar a su
 * propia caja de texto —que deja de usarse en cuanto la conversación arranca— y
 * por eso se sentía muerta a los dos mensajes.
 *
 *   window.addEventListener('tc-chat:dicho',  (e) => e.detail.texto)
 *   window.addEventListener('tc-chat:escena', (e) => e.detail.foto)
 *   window.addEventListener('tc-chat:paso',   (e) => e.detail.paso)
 *
 * Va por eventos y no por props: el widget se embebe en sitios que no
 * controlamos, y ninguno tiene por qué conocer nuestra estructura interna.
 */
function anunciar(nombre: string, detail: Record<string, unknown>): void {
  window.dispatchEvent(new CustomEvent(nombre, { detail }))
}

/**
 * Cuando el cliente da su nombre, la casa se entera.
 *
 * `visitorName` nace como "Viajero" y cambia solo cuando el asesor consigue el
 * nombre de verdad y lo guarda en la conversación. Ese instante vale: es cuando
 * el visitante deja de ser tráfico y pasa a ser alguien.
 */
watch(visitorName, (nombre) => {
  const limpio = nombre.trim()
  if (!limpio || limpio === 'Viajero') return
  anunciar('tc-chat:quien', { nombre: limpio })
})

/**
 * Lo que el asesor ya confirmó del viaje: destino, fechas, cuántos van.
 *
 * Sale de `conversations/{id}.searchSlots`, que escribe el motor DESPUÉS de
 * entenderlo. La portada antes lo adivinaba con expresiones regulares sobre el
 * texto —y adivinaba mal en cuanto alguien escribía "del 1 al 4 de noviembre"—.
 * Esto es el dato, no la suposición.
 */
watch(datosViaje, (d) => {
  if (!d) return
  anunciar('tc-chat:datos', {
    destino: d.destination ?? null,
    entrada: d.arrival ?? null,
    salida: d.departure ?? null,
    adultos: d.adults ?? null,
    menores: d.children ?? null,
  })
}, { deep: true })

/**
 * El viaje que se está armando.
 *
 * Va por su propio evento y no dentro de 'escena' porque no es una cosa que
 * pasó: es el estado de la compra, y la pizarra lo tiene que poder repintar
 * entero cada vez, incluido el caso de quedarse vacío.
 */
/**
 * Las tarifas que ya están en el viaje.
 *
 * Es lo que permite que una tarjeta diga "✓ En tu viaje" en vez de seguir
 * ofreciendo "Agregar" algo que ya se agregó. La tarjeta no puede saberlo sola:
 * vive en la conversación y el carrito vive aparte.
 */
const refsEnViaje = computed(() => carrito.value?.items.map((i) => i.ref) ?? [])

/**
 * La compra ya se cerró.
 *
 * La señal es el recibo: mientras no exista, el pago sigue pendiente. No se usa
 * un estado local porque el recibo llega por realtime y sobrevive a recargar la
 * página — volver y encontrarse el formulario de tarjeta bajo un viaje ya
 * pagado sería la peor manera de terminar una venta buena.
 */
const yaPagado = computed(() => messages.value.some((m) => m.kind === 'booking_summary'))

watch(carrito, (c) => {
  if (!c) return
  anunciar('tc-chat:carrito', {
    items: c.items.map((i) => ({
      nombre: i.titulo ?? i.nombre, tipo: i.tipo, precio: i.precio,
      foto: i.foto ?? null, incluye: i.incluye ?? [],
    })),
    total: c.total,
    moneda: c.currency,
  })
}, { deep: true })

// Se narra lo REVELADO, no lo recibido: la pizarra tiene que ir al paso de lo
// que el cliente está viendo, no adelantarse a la tarjeta que todavía no cae.
let narrados = 0
watch(revelados, (total) => {
  for (const m of messages.value.slice(narrados, total)) {
    if (m.senderType === 'visitor' && m.kind === 'text') {
      anunciar('tc-chat:dicho', { texto: m.text })
    } else if (m.kind === 'hotel_card' && m.hotelCard) {
      anunciar('tc-chat:escena', {
        tipo: 'producto',
        // El nombre corto, como en la tarjeta. En la pizarra el titular va a
        // 40px y el del proveedor ocupaba cuatro renglones.
        titulo: m.hotelCard.titulo ?? m.hotelCard.hotelName,
        detalle: m.hotelCard.subtitulo ?? m.hotelCard.duracion ?? m.hotelCard.roomName,
        foto: m.hotelCard.photos?.[0] ?? m.hotelCard.imageUrl ?? null,
      })
    } else if (m.kind === 'hotel_info' && m.hotelInfo) {
      anunciar('tc-chat:escena', {
        tipo: 'ficha',
        titulo: m.hotelInfo.name,
        detalle: m.hotelInfo.destination ?? null,
        foto: m.hotelInfo.photos?.[0] ?? m.hotelInfo.photo ?? null,
      })
    } else if (m.kind === 'circuito' && m.circuito) {
      // Un circuito manda en la pizarra igual que un producto: es el viaje del
      // que se está hablando. El recorrido dice más que la duración —"San
      // Francisco · Las Vegas" es la razón por la que alguien lo compra—.
      anunciar('tc-chat:escena', {
        tipo: 'producto',
        titulo: m.circuito.nombre,
        detalle: m.circuito.recorrido.join(' · ') || m.circuito.duracion,
        foto: m.circuito.photos?.[0] ?? null,
      })
    } else if (m.kind === 'payment') {
      anunciar('tc-chat:paso', { paso: 'pago' })
    } else if (m.kind === 'booking_summary') {
      anunciar('tc-chat:paso', { paso: 'reservado', locator: m.bookingSummary?.locator ?? null })
    }
  }
  narrados = total
})

async function send(): Promise<void> {
  const text = draft.value.trim()
  if (!text) return
  const cita = citando.value
  draft.value = ''
  citando.value = null
  await nextTick()
  ajustar()
  await sendMessage(text, cita ?? undefined)
}

/* ---------- Citar un mensaje, como en WhatsApp ----------
 *
 * El gesto lo pidió un dedo antes que nadie: probando en el móvil, la mano fue
 * sola a deslizar una tarjeta para preguntar por ELLA. Y resuelve algo que no
 * es cosmético — con tres opciones en pantalla, "¿ese tiene alberca?" obliga al
 * bot a deducir de cuál habla, y cuando falla contesta con seguridad sobre el
 * hotel equivocado.
 */
const citando = ref<CitaPayload | null>(null)
const gesto = new GestoDeCita()
/** Qué mensaje se está arrastrando y cuánto. Solo uno a la vez. */
const arrastrando = ref<string | null>(null)
const arrastreDx = ref(0)
/** El último citado, para resaltarlo un momento al saltar a él. */
const resaltado = ref<string | null>(null)

/**
 * Qué se puede citar.
 *
 * Todo lo que identifique algo de lo que se pueda hablar. Se excluyen el paso
 * de pago —es un formulario, no una frase— y la ficha del viaje, que es estado
 * y se reescribe: citarla dejaría una referencia a algo que ya cambió.
 */
function sePuedeCitar(m: UiMessage): boolean {
  return m.kind !== 'payment' && m.kind !== 'cart' && m.kind !== 'request_summary'
}

/** Cómo se llama lo citado y qué pedazo se enseña. */
function citaDe(m: UiMessage): CitaPayload {
  if (m.kind === 'hotel_card' && m.hotelCard) {
    return {
      id: m.id, titulo: m.hotelCard.hotelName,
      extracto: m.hotelCard.duracion || m.hotelCard.roomName || '',
      ...(m.hotelCard.rateKeyRef ? { ref: m.hotelCard.rateKeyRef } : {}),
    }
  }
  if (m.kind === 'hotel_info' && m.hotelInfo) {
    return {
      id: m.id, titulo: m.hotelInfo.name ?? 'Hotel',
      extracto: m.hotelInfo.destination ?? '',
      ...(m.hotelInfo.rateKeyRef ? { ref: m.hotelInfo.rateKeyRef } : {}),
    }
  }
  if (m.kind === 'circuito' && m.circuito) {
    return { id: m.id, titulo: m.circuito.nombre, extracto: m.circuito.duracion }
  }
  return {
    id: m.id,
    titulo: m.senderType === 'visitor' ? visitorName.value : quien(m),
    extracto: m.text ?? '',
  }
}

function alTocar(e: TouchEvent, m: UiMessage): void {
  // La galería se desliza de lado por su cuenta. Si el gesto empieza ahí, es
  // para ver la foto 3 — no para citar. Sin esta salida, las dos cosas pelean
  // y la que pierde es la que el cliente quería.
  if ((e.target as HTMLElement | null)?.closest('.gal-tira')) return
  if (!sePuedeCitar(m)) return
  const t = e.touches[0]
  if (!t) return
  arrastrando.value = m.id
  gesto.iniciar(t.clientX, t.clientY)
}

function alArrastrar(e: TouchEvent, m: UiMessage): void {
  if (arrastrando.value !== m.id) return
  const t = e.touches[0]
  if (!t) return
  const r = gesto.mover(t.clientX, t.clientY)
  if (!r) {
    // El gesto era vertical: se suelta el mensaje y se deja pasar el scroll.
    arrastrando.value = null
    arrastreDx.value = 0
    return
  }
  // Solo se cancela el desplazamiento cuando ya sabemos que el gesto es
  // nuestro. Cancelar de más deja la conversación imposible de recorrer.
  if (r.dx > 0 && e.cancelable) e.preventDefault()
  arrastreDx.value = r.dx
}

function alSoltar(m: UiMessage): void {
  if (arrastrando.value !== m.id) return
  const r = gesto.soltar()
  arrastrando.value = null
  arrastreDx.value = 0
  if (r.cita) {
    citando.value = citaDe(m)
    // Un golpecito: confirma sin ruido, como en cualquier chat del teléfono.
    navigator.vibrate?.(12)
    cajaEl.value?.focus()
  }
}

/** Saltar al mensaje citado y marcarlo un momento, para no perder el hilo. */
function irAlCitado(id: string): void {
  const el = listEl.value?.querySelector<HTMLElement>(`[data-mid="${id}"]`)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  resaltado.value = id
  setTimeout(() => { if (resaltado.value === id) resaltado.value = null }, 1600)
}

/**
 * La caja crece con el texto en vez de desplazarse dentro de una sola línea.
 *
 * Quien escribe "somos 2 adultos y 2 niños de 6 y 9, del 12 al 16 de marzo, con
 * alberca" en un renglón de 40 caracteres no ve lo que escribió y no lo revisa.
 * El tope de 140 px evita que la caja se coma la conversación.
 */
function ajustar(): void {
  const el = cajaEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`
}

/** Enter envía; Shift+Enter hace salto de línea, como en cualquier chat. */
function alTeclear(e: KeyboardEvent): void {
  if (e.key !== 'Enter' || e.shiftKey) return
  e.preventDefault()
  void send()
}

import type { PaidPayload } from '@/payments'

/**
 * Sube cada vez que un intento de COBRO termina, salga bien o mal.
 *
 * La tarjeta de pago lo usa para dejar de decir "Confirmando con el
 * operador…". Sin esto, un cobro que fallaba dejaba ese letrero puesto para
 * siempre y el cliente no sabía si había pagado.
 */
const cobroCerrado = ref(0)

function onAction(payload: { token: string; label: string; paid?: PaidPayload }): void {
  const esCobro = payload.token === 'pay'
  void sendAction(payload.token, payload.label, payload.paid)
    .finally(() => { if (esCobro) cobroCerrado.value++ })
}

/**
 * La página que hospeda al widget puede pedirle cosas.
 *
 * Se hace por eventos de `window` y no exponiendo métodos del componente: el
 * widget vive en Shadow DOM y se embebe en sitios que no controlamos, así que
 * un canal por eventos no obliga a nadie a conocer nuestra estructura interna
 * ni se rompe si la cambiamos.
 *
 *   window.dispatchEvent(new CustomEvent('tc-chat:abrir'))
 *   window.dispatchEvent(new CustomEvent('tc-chat:preguntar', {
 *     detail: { texto: 'Busco un paquete a Cancún' },
 *   }))
 *
 * Abrir y preguntar van juntos a propósito: mandar un mensaje con el chat
 * cerrado dejaría al cliente sin ver la respuesta.
 *
 * ## Traer a alguien desde la tienda, con lo que estaba viendo
 *
 * El sitio vende el paquete tal como se publica; personalizarlo se hace aquí.
 * Quien llega de allá viene de una ficha concreta, y empezar preguntándole "¿a
 * dónde quieres ir?" borra todo lo que ya había hecho.
 *
 *   window.dispatchEvent(new CustomEvent('tc-chat:preguntar', {
 *     detail: {
 *       texto: 'Vengo del sitio. Quiero este pero cambiarle cosas — Cancún, ' +
 *              'del 1 al 5 de febrero, 2 adultos.',
 *       cita: { titulo: 'PAQUETE "EXPERIENCIA DELFINES" | 5 DÍAS 4 NOCHES + 3 TOURS' },
 *     },
 *   }))
 *
 * La cita es el mismo mecanismo del gesto de deslizar en móvil: al asesor le
 * llega como "el cliente señaló ESTO", que es el sujeto de la frase y no un
 * dato de contexto que pueda ignorar. El destino, las fechas y cuántos van se
 * leen del texto, así que conviene que vayan escritos como los diría una
 * persona.
 */
function alAbrir(): void { isOpen.value = true }

async function alPreguntar(e: Event): Promise<void> {
  const detalle = (e as CustomEvent).detail as {
    texto?: unknown
    cita?: { titulo?: unknown; extracto?: unknown; ref?: unknown }
  } | undefined
  const texto = String(detalle?.texto ?? '').trim()
  if (!texto) return

  /* La cita que llega de fuera no apunta a ningún mensaje de este hilo —el
   * producto se vio en otra página—, así que `id` va vacío y el chip no lleva
   * salto. El asesor no lo necesita: lo que él lee es el título. */
  const titulo = String(detalle?.cita?.titulo ?? '').trim()
  const cita: CitaPayload | undefined = titulo
    ? {
      id: '',
      titulo,
      extracto: String(detalle?.cita?.extracto ?? '').trim(),
      ...(detalle?.cita?.ref ? { ref: String(detalle.cita.ref) } : {}),
    }
    : undefined

  isOpen.value = true
  await nextTick()
  await sendMessage(texto, cita)
}

onMounted(() => {
  window.addEventListener('tc-chat:abrir', alAbrir)
  window.addEventListener('tc-chat:preguntar', alPreguntar)
})
onBeforeUnmount(() => {
  window.removeEventListener('tc-chat:abrir', alAbrir)
  window.removeEventListener('tc-chat:preguntar', alPreguntar)
})

/**
 * El lanzador se presenta solo, una vez, y luego se hace chico.
 *
 * Un orbe sin palabras es bonito y no dice nada: nadie sabe que detrás hay
 * alguien que puede moverle el paquete. Y una etiqueta permanente ocupa media
 * pantalla de teléfono para siempre. Así que dice lo que hace al llegar —con
 * un respiro para no aterrizar encima de la página— y después se retira al
 * orbe, donde vuelve a asomarse al pasar el cursor.
 */
const invitando = ref(false)
let presentacion: ReturnType<typeof setTimeout> | null = null
let retirada: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  if (empotrado.value) return
  presentacion = setTimeout(() => {
    invitando.value = true
    retirada = setTimeout(() => { invitando.value = false }, 5200)
  }, 1400)
})
onBeforeUnmount(() => {
  if (presentacion) clearTimeout(presentacion)
  if (retirada) clearTimeout(retirada)
})

function toggle(): void {
  isOpen.value = !isOpen.value
  invitando.value = false
}

/* ---------- La experiencia completa ----------
 *
 * La burbuja está bien para empezar y se queda chica en cuanto hay tarjetas:
 * fotos de 300 px en una columna de 380. La pizarra a pantalla ancha es la
 * misma conversación con sitio para verla.
 *
 * La dirección NO se configura: se deduce de dónde cuelga este mismo archivo.
 * Si el sitio carga `/bot/widget.js`, la experiencia completa está en `/bot/`.
 * Eso no es un truco por ahorrarse un parámetro — es lo que hace que la
 * invitación aparezca EXACTAMENTE cuando es segura.
 *
 * Porque la conversación pertenece a un usuario anónimo que el navegador guarda
 * POR ORIGEN. Mandar a alguien a otro dominio no la continúa: la empieza de
 * cero, y el cliente que llevaba diez mensajes armando su viaje aterriza en
 * blanco. Así que si el widget se sirve desde otro origen —el sitio de UVAC
 * cargándolo de bot.uvaconline.com, por ejemplo— la invitación sencillamente no
 * se ofrece. No hay nada que configurar mal.
 */
const experienciaUrl = (() => {
  try {
    const url = new URL('./', import.meta.url)
    return url.origin === window.location.origin ? url.href : null
  } catch {
    return null
  }
})()

/** Ya estando en la pizarra, invitar a la pizarra no tiene sentido. */
const hayADondeIr = computed(() => !empotrado.value && !!experienciaUrl)

/**
 * La invitación se gana; no se ofrece de entrada.
 *
 * Mientras la conversación es texto, la burbuja alcanza y proponer mudarse es
 * ruido. En cuanto aparece la primera tarjeta con fotos, la columna angosta
 * empieza a estorbar de verdad — ese es el momento, y no antes.
 */
const CERRADA = 'tc-invitacion-cerrada'
const invitacionCerrada = ref(false)
try {
  invitacionCerrada.value = localStorage.getItem(CERRADA) === '1'
} catch { /* navegación privada: se ofrece igual, no pasa nada */ }

const hayFichas = computed(() => messages.value.some((m) => m.kind !== 'text'))
const invitaAlGrande = computed(
  () => hayADondeIr.value && hayFichas.value && !invitacionCerrada.value,
)

function cerrarInvitacion(): void {
  invitacionCerrada.value = true
  try { localStorage.setItem(CERRADA, '1') } catch { /* da igual */ }
}
</script>

<template>
  <div class="tc">
    <!-- Panel -->
    <div v-if="isOpen" class="panel" role="dialog" aria-label="Chat de reservas">
      <header class="head">
        <div class="head-id">
          <img
            v-if="!logoRoto" :src="logoUrl" class="logo" alt="UVAC Viajes"
            @error="logoRoto = true"
          />
          <span v-else class="mark">✦</span>
          <div>
            <div class="head-name">Asistente de viajes</div>
            <div class="head-sub">Responde al instante</div>
          </div>
        </div>
        <!-- Ampliar: solo aparece cuando hay a dónde ir Y es el mismo origen.
             En otro dominio la conversación empezaría de cero, así que ahí ni
             se ofrece. Ver `experienciaUrl`. -->
        <div class="head-acciones">
          <a
            v-if="hayADondeIr" class="ampliar" :href="experienciaUrl!"
            aria-label="Ver la conversación a pantalla completa"
            title="Ver a pantalla completa"
          >⤢</a>
        <!-- NO es una X.
             Una X dice "esto se acaba", y en un teléfono el chat ocupa toda la
             pantalla: quien la ve piensa que va a perder la conversación y no
             la toca. Y lo que hay detrás no es el vacío — es el resumen del
             viaje que se está armando, que es justo lo que querría ver.
             La flecha hacia abajo dice "esto se guarda ahí", que es la verdad. -->
        <button class="close" aria-label="Ver el resumen del viaje" @click="toggle">⌄</button>
        </div>
      </header>

      <div ref="listEl" class="list">
        <template v-for="m in visibles" :key="m.id">
          <!-- Pagar y quedar reservado no son "otro mensaje del bot": son los
               dos momentos que el cliente va a recordar. Se separan del hilo
               para que se vean como pasos y no se pierdan entre burbujas. -->
          <div v-if="esHito(m)" class="sep" :class="m.kind">
            <span>{{ m.kind === 'payment' ? 'Pago seguro' : 'Reserva confirmada' }}</span>
          </div>
          <div
            class="row" :class="[m.senderType, { hito: esHito(m), resaltado: resaltado === m.id }]"
            :data-mid="m.id"
            :style="arrastrando === m.id ? { transform: `translateX(${arrastreDx}px)` } : undefined"
            @touchstart.passive="alTocar($event, m)"
            @touchmove="alArrastrar($event, m)"
            @touchend="alSoltar(m)"
            @touchcancel="alSoltar(m)"
          >
            <!-- La flecha que asoma al deslizar. Aparece con el recorrido, así
                 que el gesto se explica solo la primera vez que se hace a
                 medias: nadie tiene que leer una instrucción. -->
            <span
              v-if="arrastrando === m.id" class="citar-pista" aria-hidden="true"
              :style="{ opacity: String(Math.min(arrastreDx / 56, 1)) }"
            >↩</span>

            <div class="msg-wrap">
              <span v-if="!esHito(m)" class="who">{{ quien(m) }}</span>

              <!-- Lo citado, arriba del mensaje. Se toca para volver a ello. -->
              <button
                v-if="m.citaDe" class="cita-en-msg" type="button"
                @click="m.citaDe.id && irAlCitado(m.citaDe.id)"
              >
                <b>{{ m.citaDe.titulo }}</b>
                <span v-if="m.citaDe.extracto">{{ m.citaDe.extracto }}</span>
              </button>

              <div v-if="m.kind === 'text'" class="bubble">{{ m.text }}</div>
              <RichMessage
                v-else :msg="m" :en-viaje="refsEnViaje" :pagado="yaPagado"
                :telefono-conocido="contacto?.phone" :cobro-cerrado="cobroCerrado"
                @action="onAction"
              />
            </div>

            <!-- En escritorio no hay dedo que deslice. El mismo gesto, con el
                 ratón, aparece al pasar por encima. -->
            <button
              v-if="sePuedeCitar(m)" class="citar-btn" type="button"
              aria-label="Preguntar sobre esto"
              @click="citando = citaDe(m); cajaEl?.focus()"
            >↩</button>
          </div>
        </template>
        <!-- Mientras el motor trabaja, se dice QUÉ está haciendo. La espera se
             tolera cuando se entiende; los puntos solos parecen un cuelgue. -->
        <div v-if="escribiendo" class="row bot">
          <div class="bubble typing" :class="{ conLetrero: trabajando }">
            <template v-if="trabajando">{{ trabajando }}</template>
            <template v-else>···</template>
          </div>
        </div>
      </div>

      <!-- La invitación a la pizarra.
           Aparece cuando la conversación se la gana —con la primera tarjeta con
           fotos, no antes— porque mientras es texto la burbuja alcanza y
           proponer mudarse es ruido. Se cierra y no vuelve. -->
      <div v-if="invitaAlGrande" class="invita">
        <span class="invita-orbe" aria-hidden="true"></span>
        <a class="invita-txt" :href="experienciaUrl!">
          <b>Se ve mejor en grande</b>
          <span>Tu viaje a pantalla completa, sin perder la charla</span>
        </a>
        <button
          type="button" class="invita-x"
          aria-label="No mostrar la invitación" @click="cerrarInvitacion"
        >✕</button>
      </div>

      <!-- Lo que se está citando, sobre el teclado. Es la confirmación del
           gesto: sin esto, deslizar no dejaría rastro y nadie sabría que
           funcionó. -->
      <div v-if="citando" class="cita-chip">
        <div class="cita-txt">
          <b>{{ citando.titulo }}</b>
          <span v-if="citando.extracto">{{ citando.extracto }}</span>
        </div>
        <button type="button" aria-label="Quitar la cita" @click="citando = null">✕</button>
      </div>

      <form class="input" @submit.prevent="send">
        <textarea
          ref="cajaEl"
          v-model="draft"
          rows="1"
          :placeholder="citando ? `Pregunta sobre ${citando.titulo}…` : 'Escribe tu mensaje…'"
          autocomplete="off"
          aria-label="Mensaje"
          @input="ajustar"
          @keydown="alTeclear"
        ></textarea>
        <button type="submit" :disabled="!draft.trim()" aria-label="Enviar">➤</button>
      </form>
    </div>

    <!-- Launcher: solo en modo burbuja. Empotrado, el chat ya está a la vista y
         un botón flotante encima sería un estorbo.

         NO es la burbuja clásica de mensajería. La burbuja promete "escríbenos
         y alguien te contesta"; esto ofrece otra cosa —que el paquete que estás
         mirando se puede mover a tu medida, aquí y ahora— y tiene que verse
         distinto para que se entienda distinto.

         El orbe está hecho de gradientes que giran a distinta velocidad dentro
         de una forma que se deforma sola. Ese desfase es lo que da la sensación
         líquida: nada rebota ni pulsa, todo va a la deriva. Es CSS puro, sin
         canvas ni librerías — pesa cero y no consume batería en un teléfono. -->
    <button
      v-if="!empotrado" class="launcher" :class="{ open: isOpen, invita: invitando }"
      type="button"
      :aria-label="isOpen ? 'Minimizar el asistente' : 'Personaliza tu viaje con el asistente'"
      @click="toggle"
    >
      <span class="l-orbe" aria-hidden="true">
        <span class="l-liquido"></span>
        <span class="l-brillo"></span>
        <span class="l-chispa">✦</span>
      </span>
      <span class="l-texto">{{ isOpen ? 'Minimizar' : 'Personaliza tu viaje' }}</span>
    </button>
  </div>
</template>

<style>
:host {
  /* Paleta UVAC: azul de marca, no el verde azulado con el que nació el
     widget. Un chat que no se parece al sitio se siente pegado de otro lado, y
     el cliente duda justo cuando va a dar su tarjeta. */
  --tc-azul: #1668e3;        /* acción: botones, precios, burbuja propia */
  --tc-azul-hondo: #0b3f8f;  /* hover y estados presionados */
  --tc-navy: #0a2a5e;        /* cabecera y lanzador, como el pie del sitio */
  --tc-acento: #9dc7ff;      /* texto secundario sobre azul oscuro */
  --tc-ink: #10203a;
  --tc-muted: #5b6b82;
  --tc-line: #e3e8ef;
  --tc-bg: #ffffff;
  --tc-soft: #eef4ff;
  all: initial;
}
* { box-sizing: border-box; }

.tc {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483000;
  font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Ampliar y minimizar, juntos en la esquina de la cabecera. */
.head-acciones { display: flex; align-items: center; gap: 2px; }
.ampliar {
  color: var(--tc-acento); text-decoration: none;
  font-size: 17px; line-height: 1; padding: 6px 8px; border-radius: 8px;
  transition: background .15s ease, color .15s ease;
}
.ampliar:hover { background: rgba(255,255,255,.12); color: #fff; }

/* ---------- La invitación a la pizarra ----------
 *
 * Va sobre el teclado y no dentro del hilo: no es un mensaje, es una oferta.
 * Metida entre las burbujas parecería que el asesor la dijo, y empujaría hacia
 * arriba lo que el cliente estaba leyendo.
 */
.invita {
  display: flex; align-items: center; gap: 10px;
  margin: 0 10px 8px; padding: 9px 10px;
  border-radius: 12px; border: 1px solid #d7e6ff;
  background: linear-gradient(100deg, #f2f8ff, #eef4ff);
}
/* El mismo orbe del lanzador, en chiquito: dice de dónde viene la oferta. */
.invita-orbe {
  flex: none; width: 26px; height: 26px; border-radius: 50%;
  background:
    radial-gradient(circle at 32% 30%, #7aecff, transparent 60%),
    radial-gradient(circle at 70% 70%, #a87aff, transparent 58%),
    linear-gradient(135deg, #1668e3, #0b3f8f);
  animation: tc-gira 17s linear infinite;
}
.invita-txt { flex: 1; min-width: 0; text-decoration: none; display: block; }
.invita-txt b { display: block; font-size: 12.5px; color: var(--tc-azul-hondo); }
.invita-txt span {
  display: block; font-size: 11px; color: var(--tc-muted); line-height: 1.35;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.invita-x {
  flex: none; border: 0; background: transparent; color: var(--tc-muted);
  font-size: 12px; cursor: pointer; padding: 4px 6px; line-height: 1;
}
.invita-x:hover { color: var(--tc-ink); }
@media (prefers-reduced-motion: reduce) { .invita-orbe { animation: none; } }

/* Launcher */
/* ---------- El lanzador ----------
 *
 * Una cápsula que casi siempre es solo el orbe. El texto vive dentro con
 * `max-width: 0` y se abre al invitar o al pasar el cursor: animar el ancho
 * máximo da una apertura que empuja el contenido, como algo que se despliega,
 * en vez del salto seco de mostrar y ocultar.
 */
.launcher {
  margin-left: auto;
  display: flex; align-items: center; gap: 0;
  padding: 7px; border: none; cursor: pointer;
  border-radius: 999px;
  background: rgba(10, 42, 94, .92);
  /* El vidrio de detrás: lo que hace que se sienta encima de la página y no
     pegado sobre ella. */
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  box-shadow:
    0 10px 30px rgba(10, 42, 94, .38),
    inset 0 1px 0 rgba(255, 255, 255, .18);
  color: #fff;
  font: inherit; font-size: 13.5px; font-weight: 600; letter-spacing: .1px;
  transition: box-shadow .3s ease, transform .3s cubic-bezier(.2, .9, .3, 1.2);
}
.launcher:hover { transform: translateY(-2px); box-shadow: 0 14px 38px rgba(10, 42, 94, .46); }
.launcher:active { transform: translateY(0) scale(.98); }
.launcher:focus-visible { outline: 2px solid #9dc7ff; outline-offset: 3px; }

/* El orbe: tres capas que giran a distinta velocidad. */
.l-orbe {
  position: relative; flex: none;
  width: 46px; height: 46px; border-radius: 50%;
  display: grid; place-items: center; overflow: hidden;
  background: radial-gradient(circle at 30% 28%, #4aa8ff 0%, #1668e3 42%, #0b3f8f 100%);
  box-shadow: inset 0 0 18px rgba(0, 0, 0, .35);
}

/* La forma que se deforma. Animar `border-radius` es lo que hace lo líquido:
   la mancha cambia de silueta mientras gira, y como el giro y la deformación
   tardan distinto, nunca se repite igual a la vista. */
.l-liquido {
  position: absolute; inset: -32%;
  background:
    radial-gradient(circle at 32% 30%, rgba(122, 236, 255, .95), transparent 58%),
    radial-gradient(circle at 72% 68%, rgba(168, 122, 255, .85), transparent 56%),
    radial-gradient(circle at 52% 88%, rgba(80, 255, 214, .55), transparent 52%);
  filter: blur(5px);
  animation: tc-liquido 11s ease-in-out infinite, tc-gira 17s linear infinite;
}
@keyframes tc-liquido {
  0%,100% { border-radius: 42% 58% 63% 37% / 41% 44% 56% 59%; }
  33%     { border-radius: 62% 38% 34% 66% / 63% 38% 62% 37%; }
  66%     { border-radius: 37% 63% 56% 44% / 55% 62% 38% 45%; }
}
@keyframes tc-gira { to { transform: rotate(360deg); } }

/* El barrido de luz: cruza la esfera cada tanto, como un reflejo. */
.l-brillo {
  position: absolute; inset: 0;
  background: linear-gradient(115deg, transparent 38%, rgba(255,255,255,.55) 50%, transparent 62%);
  transform: translateX(-120%);
  animation: tc-brillo 6.5s ease-in-out infinite;
}
@keyframes tc-brillo {
  0%, 62% { transform: translateX(-120%); }
  78%,100% { transform: translateX(120%); }
}

.l-chispa {
  position: relative; font-size: 17px; line-height: 1;
  color: #fff; text-shadow: 0 0 12px rgba(255,255,255,.75);
  animation: tc-respira 4.5s ease-in-out infinite;
}
@keyframes tc-respira {
  0%,100% { opacity: .92; transform: scale(1); }
  50%     { opacity: 1;   transform: scale(1.12); }
}

/* El texto: cerrado por omisión, se abre al invitar y al pasar el cursor. */
.l-texto {
  max-width: 0; opacity: 0; overflow: hidden; white-space: nowrap;
  transition: max-width .45s cubic-bezier(.2,.9,.3,1), opacity .3s ease, padding .45s ease;
  padding-right: 0;
}
.launcher.invita .l-texto,
.launcher:hover .l-texto,
.launcher:focus-visible .l-texto {
  max-width: 220px; opacity: 1; padding: 0 14px 0 10px;
}

/* Abierto: el orbe se apaga. Ya está adentro, no hay nada que invitar. */
.launcher.open .l-orbe { filter: saturate(.35) brightness(.85); }
.launcher.open .l-liquido, .launcher.open .l-brillo { animation-play-state: paused; }

/* Quien pidió menos movimiento recibe el mismo botón, quieto. */
@media (prefers-reduced-motion: reduce) {
  .l-liquido, .l-brillo, .l-chispa { animation: none; }
  .l-liquido { filter: blur(6px); }
  .launcher, .l-texto { transition: none; }
}

/* Panel */
.panel {
  width: 380px; max-width: calc(100vw - 40px);
  height: 560px; max-height: calc(100vh - 120px);
  background: var(--tc-bg); border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.22);
  display: flex; flex-direction: column; overflow: hidden;
  margin-bottom: 14px; border: 1px solid var(--tc-line);
}

.head {
  /* Navy como el pie del sitio de UVAC: el azul de acción se reserva para lo
     que se toca (botones, precios), no para superficies grandes. */
  background: var(--tc-navy); color: #fff;
  padding: 14px 16px; display: flex; align-items: center; justify-content: space-between;
}
.head-id { display: flex; align-items: center; gap: 10px; }
.mark { font-size: 20px; color: #fff; }
/* Fondo blanco tras el logo: la marca es azul sobre blanco y sobre navy se
   perdería. Es cómo se ve en el sitio, no un invento. */
.logo {
  width: 30px; height: 30px; border-radius: 50%; flex: none;
  background: #fff; padding: 3px; box-sizing: border-box; object-fit: contain;
}
.head-name { font-weight: 600; font-size: 15px; }
.head-sub { font-size: 11.5px; color: var(--tc-acento); }
.close { background: transparent; border: none; color: var(--tc-acento); font-size: 16px;
  cursor: pointer; line-height: 1; padding: 6px 10px; border-radius: 8px; }
.close:hover { background: rgba(255, 255, 255, .12); }

/* Lista de mensajes */
.list { flex: 1; overflow-y: auto; padding: 16px; background: #f5f8fc;
  display: flex; flex-direction: column; gap: 10px; }
.row { display: flex; position: relative; }
/* La transición solo al soltar: durante el arrastre el mensaje sigue al dedo
   sin retraso, que es lo que hace que se sienta agarrado y no controlado. */
.row:not([style*="translateX"]) { transition: transform .18s ease-out, background .4s ease; }

/* ---------- Citar un mensaje ---------- */
/* La flecha que asoma bajo el mensaje mientras se desliza. Va detrás y a la
   izquierda: se descubre con el recorrido, así el gesto se explica solo la
   primera vez que alguien lo hace a medias. */
.citar-pista { position: absolute; left: 2px; top: 50%; transform: translateY(-50%);
  font-size: 15px; color: var(--tc-azul); pointer-events: none; }
.row.visitor .citar-pista { left: auto; right: 2px; }

/* En escritorio no hay dedo. El mismo gesto con el ratón, al pasar encima. */
.citar-btn { opacity: 0; transition: opacity .12s ease;
  align-self: center; margin: 0 4px; border: none; background: transparent;
  color: var(--tc-muted); font-size: 14px; cursor: pointer; padding: 4px 6px;
  border-radius: 7px; }
.row:hover .citar-btn { opacity: 1; }
.citar-btn:hover { background: var(--tc-soft); color: var(--tc-azul); }
.row.visitor .citar-btn { order: -1; }
/* Con dedo el botón sobra: el gesto ya está, y un ícono más en cada mensaje
   ensucia una conversación que se lee en una pantalla chica. */
@media (hover: none) { .citar-btn { display: none; } }

/* Lo citado, dentro del mensaje que lo cita. */
.cita-en-msg { display: flex; flex-direction: column; gap: 1px; text-align: left;
  border: none; border-left: 3px solid var(--tc-azul); background: var(--tc-soft);
  border-radius: 8px; padding: 5px 9px; margin-bottom: 4px; cursor: pointer;
  max-width: 100%; font: inherit; }
.cita-en-msg b { font-size: 11.5px; color: var(--tc-azul-hondo); }
.cita-en-msg span { font-size: 11px; color: var(--tc-muted);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Al saltar a un mensaje citado se marca un momento: sin eso, el salto deja al
   lector sin saber cuál de todos era. */
.row.resaltado .bubble, .row.resaltado .rm-card, .row.resaltado .rm-circ {
  background: #fff6da; }

/* El chip sobre el teclado: la confirmación de que el gesto funcionó. */
.cita-chip { display: flex; align-items: center; gap: 10px;
  margin: 0 10px; padding: 7px 10px; border-radius: 10px 10px 0 0;
  background: var(--tc-soft); border-left: 3px solid var(--tc-azul); }
.cita-txt { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
.cita-txt b { font-size: 12px; color: var(--tc-azul-hondo); }
.cita-txt span { font-size: 11.5px; color: var(--tc-muted);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cita-chip button { border: none; background: transparent; cursor: pointer;
  color: var(--tc-muted); font-size: 13px; padding: 4px 6px; line-height: 1; }
.msg-wrap { display: flex; flex-direction: column; max-width: 88%; }
.row.visitor .msg-wrap { align-items: flex-end; }
.who { font-size: 10.5px; font-weight: 700; color: var(--tc-muted); margin: 0 4px 3px;
  letter-spacing: 0.02em; }
.row.bot .who, .row.agent .who { color: var(--tc-azul); }
.row.visitor { justify-content: flex-end; }
.row.bot, .row.agent, .row.system { justify-content: flex-start; }

  .bubble {
  max-width: 100%; padding: 9px 13px; border-radius: 14px;
  font-size: 14px; line-height: 1.45; white-space: pre-wrap; word-wrap: break-word;
  background: #fff; color: var(--tc-ink); border: 1px solid var(--tc-line);
}
.row.visitor .bubble { background: var(--tc-azul); color: #fff; border-color: var(--tc-azul); }
.typing { letter-spacing: 2px; color: var(--tc-muted); }
/* Con letrero deja de ser un adorno y pasa a ser una frase: se lee, así que
   se le quita el espaciado de puntos y se le pone un pulso discreto. */
.typing.conLetrero {
  letter-spacing: 0; font-size: 13px; font-style: italic;
  animation: tc-latir 1.6s ease-in-out infinite;
}
@keyframes tc-latir { 0%, 100% { opacity: .55 } 50% { opacity: 1 } }
@media (prefers-reduced-motion: reduce) { .typing.conLetrero { animation: none } }

/* Rich messages ocupan ancho de columna */
.row .rm-card, .row .rm-pay, .row .rm-book, .row .rm-circ { width: 100%; }

.rm-card { background: #fff; border: 1px solid var(--tc-line); border-radius: 14px; overflow: hidden;
  transition: border-color .25s ease, box-shadow .25s ease, background .25s ease; }
/* Elegido. El cambio tiene que verse SIN leer: de un vistazo, entre seis
   tarjetas, se distingue cuál ya está adentro. */
.rm-card.elegido {
  border-color: #2f7d57;
  background: linear-gradient(180deg, #f4fbf7 0%, #fff 42%);
  box-shadow: 0 6px 20px rgba(47, 125, 87, .14);
}
/* Qué incluye el paquete: lo que de verdad decide la compra */
.rm-inc { margin: 9px 0 0; padding: 0 0 0 16px; font-size: 12.5px; line-height: 1.5; color: var(--tc-muted); }
.rm-inc li { margin: 1px 0; }
.rm-card-body { padding: 12px 14px 6px; }
.rm-hotel { font-weight: 700; font-size: 14.5px; color: var(--tc-ink); }
.rm-room { font-size: 12.5px; color: var(--tc-muted); margin-top: 2px; }
.rm-tags { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.rm-tag { font-size: 10.5px; font-weight: 600; padding: 2px 7px; border-radius: 20px;
  background: var(--tc-soft); color: var(--tc-azul-hondo); }
.rm-tag.ok { background: #e5f2ea; color: #2f7d57; }
.rm-tag.no { background: #f6e9e9; color: #b23a3a; }
.rm-card-foot { display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; border-top: 1px solid var(--tc-line); background: #f8fafd; }
.rm-price { font-weight: 700; font-size: 15px; color: var(--tc-azul); }
.rm-unit { font-size: 11px; color: var(--tc-muted); font-weight: 400; margin-left: 3px; }

.rm-btn { background: var(--tc-azul); color: #fff; border: none; border-radius: 9px;
  padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.rm-btn:hover { background: var(--tc-azul-hondo); }
/* Ya está en el viaje: deja de invitar y confirma. No se apaga en gris —eso se
   lee como "no disponible"— sino en verde, que es lo que pasó. */
.rm-btn.puesto {
  background: #eaf5ef; color: #2f7d57; border: 1px solid #b6ddc7;
  cursor: default; opacity: 1;
}
.rm-btn.wide { width: 100%; padding: 11px; margin-top: 4px; }

/* ---------- Circuitos de salida fija ----------
   Comparte el cuerpo y el pie de la tarjeta de producto: es un producto más y
   debe verse como tal. Lo que cambia es lo único que de verdad cambia — la
   lista de salidas, porque la primera pregunta de este producto siempre es
   "¿cuándo sale?" y con una sola fecha habría que contestar "no hay" cuando en
   realidad sí hay, en marzo. */
.rm-circ { background: #fff; border: 1px solid var(--tc-line); border-radius: 14px; overflow: hidden; }
.rm-sal-t { margin-top: 11px; font-size: 10.5px; font-weight: 700; letter-spacing: .07em;
  text-transform: uppercase; color: var(--tc-muted); }
.rm-sal { list-style: none; margin: 5px 0 0; padding: 0; }
.rm-sal li { display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  padding: 6px 0; border-bottom: 1px solid var(--tc-line); }
.rm-sal li:last-child { border-bottom: none; }
.rm-sal-f { font-size: 12.5px; color: var(--tc-ink); font-weight: 600; }
.rm-sal-p { font-size: 13px; font-weight: 700; color: var(--tc-azul); white-space: nowrap; }
/* Una salida sin tarifa para esta ocupación no enseña un número que no es suyo:
   dice que falta confirmarlo, en gris y sin peso. */
.rm-sal-p.pendiente { font-size: 11.5px; font-weight: 500; color: var(--tc-muted);
  white-space: normal; text-align: right; }
/* Sin «desde» completo no se pinta un número: se invita a preguntar. */
.rm-price.sin { font-weight: 500; }
/* "Últimos espacios", nunca "quedan 2": el número delata cuánto compramos. */
.rm-ult { display: inline-block; margin-left: 7px; font-size: 10px; font-weight: 700;
  padding: 2px 7px; border-radius: 20px; background: #fdefe6; color: #b4571f;
  letter-spacing: .02em; vertical-align: middle; }

/* ---------- El viaje que se está armando ----------
   Un carrito no es un mensaje más: es el estado de la compra. Por eso ocupa el
   ancho completo, va marcado con el azul de marca y se distingue de las
   tarjetas de producto que lo rodean. */
.rm-cart {
  background: #fff; border: 1px solid var(--tc-azul); border-radius: 14px;
  padding: 13px 14px 14px; box-shadow: 0 6px 18px rgba(22, 104, 227, 0.1);
}
.row .rm-cart { width: 100%; }
.rm-cart-head {
  display: flex; align-items: center; justify-content: space-between;
  font-weight: 700; font-size: 13px; color: var(--tc-azul-hondo); margin-bottom: 8px;
}
.rm-cart-n {
  background: var(--tc-soft); color: var(--tc-azul-hondo); border-radius: 20px;
  font-size: 11px; padding: 1px 8px; font-weight: 700;
}
.rm-cart-row {
  display: flex; align-items: center; gap: 8px; padding: 7px 0;
  border-bottom: 1px dashed var(--tc-line);
}
.rm-cart-tipo {
  flex: none; font-size: 9.5px; font-weight: 800; letter-spacing: .04em;
  text-transform: uppercase; padding: 2px 6px; border-radius: 5px;
  background: var(--tc-soft); color: var(--tc-azul-hondo);
}
.rm-cart-tipo.hotel { background: #e8f0ff; }
.rm-cart-tipo.tour { background: #e5f2ea; color: #2f7d57; }
.rm-cart-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.rm-cart-txt b {
  font-size: 12.5px; color: var(--tc-ink); font-weight: 600; line-height: 1.3;
  /* Los nombres del proveedor vienen larguísimos y en mayúsculas. */
  overflow: hidden; text-overflow: ellipsis; display: -webkit-box;
  -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.rm-cart-txt span {
  font-size: 11px; color: var(--tc-muted); line-height: 1.35;
  overflow: hidden; text-overflow: ellipsis; display: -webkit-box;
  -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
/* La unidad del precio va pegada al renglón y con su propio peso: es lo que
   evita que el importe se lea como "por persona". */
.rm-cart-txt span.rm-cart-cubre { color: var(--tc-ink); opacity: .72; margin-top: 1px; }
/* El aviso de la tarjeta: legible, no decorativo. Es lo que evita que alguien
   agregue una recomposición creyendo que compró el paquete publicado. */
.rm-aviso {
  margin: 6px 0 0; padding: 7px 9px; border-radius: 8px;
  background: #fff6e5; border: 1px solid #f0d8a8; color: #7a5310;
  font-size: 11.5px; line-height: 1.4;
}
.rm-cart-precio {
  flex: none; font-size: 12.5px; font-weight: 700; color: var(--tc-ink);
  font-variant-numeric: tabular-nums;
}
.rm-cart-x {
  flex: none; border: 0; background: transparent; color: var(--tc-muted);
  font-size: 13px; cursor: pointer; padding: 2px 4px; line-height: 1;
}
.rm-cart-x:hover { color: #b23a3a; }
.rm-cart-total {
  display: flex; align-items: baseline; justify-content: space-between;
  padding: 10px 0 12px; font-size: 13px; color: var(--tc-muted);
}
.rm-cart-total b { font-size: 17px; color: var(--tc-azul); font-variant-numeric: tabular-nums; }
.rm-cart-nota { margin: 8px 0 0; font-size: 11px; color: var(--tc-muted); text-align: center; }

/* ---------- Los dos hitos: pagar y quedar reservado ----------
   Un cliente que llega al pago necesita saber, sin leer, que cambió de terreno:
   ya no está viendo opciones, está entregando dinero. Y la confirmación tiene
   que verse distinta de todo lo demás para que la reconozca al volver al chat
   una semana después buscando su localizador. */
.sep {
  display: flex; align-items: center; gap: 10px; margin: 14px 2px 2px;
  font-size: 10.5px; font-weight: 800; letter-spacing: .09em; text-transform: uppercase;
  color: var(--tc-azul-hondo);
}
.sep::before, .sep::after { content: ''; height: 1px; flex: 1; background: var(--tc-line); }
.sep.booking_summary { color: #2f7d57; }
.row.hito .msg-wrap { max-width: 100%; width: 100%; }

.rm-pay {
  background: #fff; border: 1px solid var(--tc-azul); border-top: 3px solid var(--tc-azul);
  border-radius: 14px; padding: 14px;
  box-shadow: 0 8px 22px rgba(22, 104, 227, 0.13);
}
.rm-pay-head { display: flex; align-items: baseline; justify-content: space-between; }
.rm-pay-title { font-weight: 700; font-size: 14px; color: var(--tc-ink); }
.rm-pay-amount { font-weight: 700; font-size: 17px; color: var(--tc-azul); }
.rm-pay-note { font-size: 12px; color: var(--tc-muted); margin: 8px 0 10px; line-height: 1.4; }
.rm-pay-secure { font-size: 10.5px; color: var(--tc-muted); margin-top: 9px; text-align: center; }

/* El WhatsApp del titular. No es un "por si acaso": sin él la reserva queda
   retenida del lado del operador, así que el pago no aparece hasta tenerlo. */
.rm-wa { display: block; margin: 2px 0 10px; }
.rm-wa span { display: block; font-size: 12.5px; color: var(--tc-ink); margin-bottom: 6px; font-weight: 600; }
.rm-wa input {
  width: 100%; box-sizing: border-box;
  border: 1px solid var(--tc-line); border-radius: 9px;
  padding: 10px 12px; font: inherit; font-size: 14px; color: var(--tc-ink);
  background: #fff; transition: border-color .2s ease;
}
.rm-wa input:focus { outline: none; border-color: var(--tc-azul); }
.rm-wa input.falta { border-color: #d08a3c; }
.rm-wa-nota { font-size: 11.5px; color: var(--tc-muted); margin: 0; line-height: 1.45; }

/* Cobrando: la barra no mide nada —no sabemos cuánto falta— pero dice que algo
   está pasando, que es la pregunta real de esos segundos. */
/* El cobro que no cerró. Se ve claro pero sin alarmar: no perdió dinero, y
   decírselo con rojo de emergencia lo asustaría más de lo que ayuda. */
.rm-pay-falla { font-size: 14px; font-weight: 600; color: #a4442f; margin: 8px 0 4px; }
.rm-pay-otra {
  border: 1px solid var(--tc-line); background: #fff; color: var(--tc-ink);
  border-radius: 9px; padding: 8px 14px; font-size: 13px; font-weight: 600;
  cursor: pointer; margin-top: 4px;
}
.rm-pay-otra:hover { background: #f6f8fb; }

.rm-pay-barra { height: 3px; border-radius: 3px; background: #e6eefc; overflow: hidden; }
.rm-pay-barra span {
  display: block; height: 100%; width: 40%; border-radius: 3px;
  background: linear-gradient(90deg, transparent, var(--tc-azul), transparent);
  animation: tc-corre 1.1s ease-in-out infinite;
}
@keyframes tc-corre {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}
/* Pagado: la tarjeta se apaga y cede el protagonismo al recibo de abajo. */
.rm-pay.hecho { border-color: #b6ddc7; border-top-color: #2f7d57; box-shadow: none; }
.rm-pay.hecho .rm-pay-title { color: #2f7d57; }
.rm-pay.hecho .rm-pay-amount { color: #2f7d57; }
@media (prefers-reduced-motion: reduce) {
  .rm-pay-barra span { animation: none; width: 100%; opacity: .5; }
}

/* Formulario de tarjeta (tokeniza en el navegador) */
.cf { display: flex; flex-direction: column; gap: 8px; }
.cf-row { display: flex; gap: 8px; align-items: center; }
.cf input {
  width: 100%; border: 1px solid var(--tc-line); border-radius: 9px;
  padding: 10px 12px; font-size: 14px; color: var(--tc-ink); outline: none;
  font-variant-numeric: tabular-nums;
}
.cf input:focus { border-color: var(--tc-azul); }
.cf-num { position: relative; }
.cf-brand {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--tc-azul);
  pointer-events: none; letter-spacing: 0.02em;
}
.cf-half { flex: 1; }
.cf-err { font-size: 11.5px; color: #b23a3a; }
.cf-demo { font-size: 10.5px; color: var(--tc-muted); text-align: center; margin-top: 2px; }

/* Ficha informativa de hotel (foto + instalaciones reales) */
.rm-info { background: #fff; border: 1px solid var(--tc-line); border-radius: 14px; overflow: hidden; }
.row .rm-info { width: 100%; }
/* ---------- Galería ----------
   Deslizaba bien, pero nada decía que hubiera más fotos: el cliente veía la
   primera y se quedaba con esa. El contador y los puntos son el idioma del
   carrete del teléfono — no hay que explicarlos. */
.gal { position: relative; background: var(--tc-soft); }
.gal-tira {
  display: flex; gap: 4px; overflow-x: auto; scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  /* Firefox pintaba su barra de desplazamiento con flechas y todo —eso era el
     "scroll retro"—. `::-webkit-scrollbar` solo la esconde en Chrome y Safari. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-right: 4px;
}
.gal-tira::-webkit-scrollbar { height: 0; width: 0; }
.gal-img {
  height: 150px; object-fit: cover; display: block;
  /* 91%, no 100%: la siguiente foto se asoma por el borde. Es la invitación a
     deslizar más honesta que existe — no hay que explicarla ni dibujarla. */
  flex: 0 0 91%;
  scroll-snap-align: start;
  border-radius: 2px;
}
/* Flechas redondas. Un punto de 7 px no es un objetivo razonable para un
   cursor —y en escritorio no hay dedo que deslice—, así que los puntos se
   quedan como indicador y el clic se hace aquí. */
.gal-flecha {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 32px; height: 32px; border-radius: 50%; border: 0; cursor: pointer;
  background: rgba(255, 255, 255, .92); color: var(--tc-navy);
  font-size: 20px; line-height: 1; padding: 0 0 3px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .3);
  transition: opacity .15s ease, background .15s ease;
}
.gal-flecha:hover { background: #fff; }
.gal-flecha.izq { left: 8px; }
.gal-flecha.der { right: 8px; }
.gal-flecha:disabled { opacity: 0; pointer-events: none; }

.gal-cuenta {
  position: absolute; top: 8px; right: 8px;
  background: rgba(10, 42, 94, .74); color: #fff;
  font-size: 11px; font-weight: 700; letter-spacing: .02em;
  padding: 3px 8px; border-radius: 20px; backdrop-filter: blur(3px);
  pointer-events: none;
}
.gal-puntos {
  position: absolute; left: 0; right: 0; bottom: 8px;
  display: flex; justify-content: center; gap: 6px;
  /* Una banda oscura detrás: sobre una foto de playa, unos puntos blancos se
     pierden por completo. */
  padding: 5px 0;
  background: linear-gradient(to top, rgba(0,0,0,.35), transparent);
}
.gal-punto {
  width: 7px; height: 7px; padding: 0; border: 0; border-radius: 50%; cursor: pointer;
  background: rgba(255, 255, 255, .6); box-shadow: 0 1px 4px rgba(0,0,0,.6);
  transition: width .2s ease, background .2s ease;
}
.gal-punto.activo { width: 20px; border-radius: 4px; background: #fff; }
.rm-info-body { padding: 11px 14px 13px; }
.rm-info-name { font-weight: 700; font-size: 14.5px; color: var(--tc-ink); line-height: 1.3; }
.rm-info-meta { display: flex; gap: 12px; flex-wrap: wrap; font-size: 11.5px; color: var(--tc-muted); margin-top: 4px; }
/* Por qué se lo proponemos: la voz del asesor dentro de la tarjeta. */
.rm-info-why { margin-top: 9px; font-size: 12.5px; line-height: 1.45; color: var(--tc-ink);
  border-left: 3px solid var(--tc-sand); padding-left: 9px; }

/* Ficha de solicitud (resumen en el chat) */
.rm-req { background: #fff; border: 1px solid var(--tc-line); border-radius: 14px; padding: 13px 15px; }
.row .rm-req { width: 100%; }
.rm-req-head { font-weight: 700; font-size: 13px; color: var(--tc-azul-hondo); margin-bottom: 8px; }
.rm-req-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; padding: 4px 0; color: var(--tc-muted); border-bottom: 1px dashed var(--tc-line); }
.rm-req-row:last-child { border-bottom: 0; }
.rm-req-row b { color: var(--tc-ink); font-weight: 600; text-align: right; }

/* ---------- El recibo ----------
 *
 * Es el único momento de toda la conversación en que algo salió del todo bien,
 * y hasta ahora se veía como una tarjeta más con un borde verde. Aquí sí toca
 * celebrar: verde sólido, un destello que cruza una vez, y el resto de la
 * conversación en blanco alrededor para que este bloque cante solo. */
.rm-book {
  position: relative; overflow: hidden;
  background: linear-gradient(155deg, #2f9c68 0%, #217d51 55%, #17603e 100%);
  border: none; border-radius: 16px; padding: 16px;
  color: #fff;
  box-shadow: 0 14px 34px rgba(23, 96, 62, .34), inset 0 1px 0 rgba(255,255,255,.22);
  animation: tc-llega .5s cubic-bezier(.2,.9,.3,1.2);
}
@keyframes tc-llega {
  from { opacity: 0; transform: translateY(10px) scale(.98); }
  to   { opacity: 1; transform: none; }
}
/* El destello: cruza UNA vez al aparecer y se va. Si se repitiera sería un
   anuncio; una sola vez es un "¡listo!". */
.rm-book-brillo {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(115deg, transparent 36%, rgba(255,255,255,.30) 50%, transparent 64%);
  transform: translateX(-120%);
  animation: tc-destello 1.5s .35s cubic-bezier(.3,.7,.3,1) 1 both;
}
@keyframes tc-destello { to { transform: translateX(120%); } }

.rm-book-check {
  display: flex; align-items: center; gap: 8px;
  font-weight: 800; font-size: 15px; letter-spacing: -.2px; color: #fff;
}
.rm-book-tache {
  display: grid; place-items: center; flex: none;
  width: 22px; height: 22px; border-radius: 50%;
  background: rgba(255,255,255,.22); font-size: 13px;
}
.rm-book-hotel { font-weight: 700; font-size: 15px; color: #fff; margin: 8px 0 12px; opacity: .96; }
.rm-book-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px;
  padding: 5px 0; color: rgba(255,255,255,.72); }
.rm-book-row b { color: #fff; font-weight: 600; text-align: right; }
.rm-book-row.total {
  border-top: 1px solid rgba(255,255,255,.28); margin-top: 8px; padding-top: 10px;
}
.rm-book-row.total span, .rm-book-row.total b { font-size: 14px; color: #fff; font-weight: 700; }

@media (prefers-reduced-motion: reduce) {
  .rm-book { animation: none; }
  .rm-book-brillo { display: none; }
}

/* Input */
.input { display: flex; gap: 8px; padding: 12px; border-top: 1px solid var(--tc-line); background: #fff;
  align-items: flex-end; }
.input textarea {
  flex: 1; border: 1px solid var(--tc-line); border-radius: 10px; padding: 10px 12px;
  font-size: 14px; outline: none; color: var(--tc-ink); font-family: inherit;
  line-height: 1.4; resize: none; overflow-y: auto; max-height: 140px;
}
.input textarea:focus { border-color: var(--tc-azul); }
.input button { background: var(--tc-azul); color: #fff; border: none; border-radius: 10px;
  width: 42px; height: 40px; flex: none; cursor: pointer; font-size: 15px; }
.input button:disabled { opacity: .45; cursor: default; }

/* ---------- Modo empotrado ----------
   El mismo componente, otra escala: aquí el chat no es un accesorio de la
   página, es a lo que la gente vino. Llena su columna, sin esquinas redondeadas
   ni sombra flotante —eso es lenguaje de burbuja— y con más aire para leer. */
:host([modo="panel"]) { display: block; height: 100%; }
:host([modo="panel"]) .tc {
  position: static; inset: auto; height: 100%;
  /* El contenedor no debe atrapar clics: en móvil se estira a pantalla completa
     y taparía la portada aun con el panel cerrado. */
  pointer-events: none;
}
:host([modo="panel"]) .panel {
  pointer-events: auto;
  width: 100%; max-width: none; height: 100%; max-height: none;
  margin: 0; border: 0; border-radius: 0; box-shadow: none;
}
:host([modo="panel"]) .head { padding: 16px 20px; }
:host([modo="panel"]) .list { padding: 22px 20px; gap: 12px; }
:host([modo="panel"]) .msg-wrap { max-width: 82%; }
:host([modo="panel"]) .bubble { font-size: 14.5px; padding: 10px 14px; }
:host([modo="panel"]) .input { padding: 14px 16px 16px; }
:host([modo="panel"]) .input textarea { font-size: 15px; padding: 12px 14px; }
/* En escritorio la columna es el chat: cerrarla dejaría un hueco sin nada. */
:host([modo="panel"]) .close { display: none; }

@media (max-width: 1023px) {
  /* Teléfono: pantalla completa, como escribirle a tu asesor por WhatsApp.
     Un tercio de pantalla en un teléfono no da para conversar. */
  :host([modo="panel"]) { height: auto; }
  :host([modo="panel"]) .tc { position: fixed; inset: 0; z-index: 2147483000; }
  /* Grande y con área de toque: es el único camino de vuelta a la pizarra. */
  :host([modo="panel"]) .close { display: block; font-size: 26px; padding: 2px 10px 8px; }
  :host([modo="panel"]) .input { padding-bottom: max(16px, env(safe-area-inset-bottom)); }
  :host([modo="panel"]) .head { padding-top: max(16px, env(safe-area-inset-top)); }
}
</style>
